import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Col, Row, Space } from 'antd';
import { useField } from 'formik';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { OptionKind } from '../../../../api/types';
import ArrayField from '../../../../components/fields/ArrayField';
import MultiPictureWallUploadField from '../../../../components/fields/MultiPictureWallUploadField';
import TranslatedTextAreaField from '../../../../components/fields/TranslatedTextAreaField';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import FormFields from '../../../../themes/admin/Fields/FormFields';
import { allowedExtensions } from '../../../../utilities/extensions';
import useSystemOptions from '../../../../utilities/useSystemOptions';
import useSystemSwitchData from '../../../../utilities/useSystemSwitchData';
import OptionTypeComboField from './OptionTypeComboField';
import OptionTypeMultiSelectField from './OptionTypeMultiSelectField';
import OptionTypeSingleSelectField from './OptionTypeSingleSelectField';
import { type OptionBlockFormValues, type OptionBlockValue, getOptionBlockInput } from './utils';

type NameProps = {
    prefix: string;
    index: number;
    optionBlocks: OptionBlockValue[];
    remove: (index: number) => void;
    swap: (indexA: number, indexB: number) => void;
    disabled?: boolean;
};

type PrefixProps = {
    prefix: string;
    disabled?: boolean;
};

const BlockTypeRenderer = ({ prefix, disabled = false }: PrefixProps) => {
    const [, typeMeta] = useField<OptionKind>(`${prefix}optionSettings.type`);

    switch (typeMeta.value) {
        case OptionKind.SingleSelect:
            return (
                <OptionTypeSingleSelectField
                    disabled={disabled}
                    prefix={`${prefix}optionSettings.singleSelectOptionSettingsInput`}
                />
            );

        case OptionKind.MultiSelect:
            return (
                <OptionTypeMultiSelectField
                    disabled={disabled}
                    prefix={`${prefix}optionSettings.multiSelectOptionSettingsInput`}
                />
            );

        case OptionKind.Combo:
            return (
                <OptionTypeComboField disabled={disabled} prefix={`${prefix}optionSettings.comboOptionSettingsInput`} />
            );

        default:
            return null;
    }
};

const ReorderingButtons = ({
    index,
    optionBlocks,
    swap,
}: { index: number } & Pick<NameProps, 'optionBlocks' | 'swap'>) => {
    const isFirst = index === 0;
    const isLast = index === optionBlocks.length - 1;

    const onSwap = useCallback(
        (increment: number) => {
            swap(index, index + increment);
        },
        [index, swap]
    );

    return (
        <>
            {!isFirst && <Button icon={<ArrowUpOutlined />} onClick={() => onSwap(-1)} />}
            {!isLast && <Button icon={<ArrowDownOutlined />} onClick={() => onSwap(1)} />}
        </>
    );
};

const OptionBlockItem = ({ prefix, index, optionBlocks, remove, swap, disabled }: NameProps) => {
    const { t } = useTranslation(['variantConfiguratorDetails']);

    const [, isActiveMeta] = useField<boolean>(`${prefix}isActive`);

    const { yesNoSwitch } = useSystemSwitchData();
    const { configurator } = useSystemOptions();

    return (
        <CollapsibleWrapper
            defaultActiveKey={t('variantConfiguratorDetails:sections.options.option', { number: index + 1 })}
        >
            <Panel
                key={t('variantConfiguratorDetails:sections.options.option', { number: index + 1 })}
                extra={
                    !disabled && (
                        <>
                            <ReorderingButtons index={index} optionBlocks={optionBlocks} swap={swap} />
                            <Button icon={<DeleteOutlined />} onClick={() => remove(index)} />
                        </>
                    )
                }
                header={t('variantConfiguratorDetails:sections.options.option', { number: index + 1 })}
            >
                <Row gutter={14}>
                    <Col lg={8} xs={24}>
                        <FormFields.SwitchField
                            {...yesNoSwitch}
                            {...t('variantConfiguratorDetails:fields.options.isActive', {
                                returnObjects: true,
                            })}
                            disabled={disabled}
                            name={`${prefix}isActive`}
                        />
                    </Col>
                    {isActiveMeta.value && (
                        <>
                            <Col lg={8} xs={24}>
                                <FormFields.TranslatedInputField
                                    {...t('variantConfiguratorDetails:fields.options.sectionTitle', {
                                        returnObjects: true,
                                    })}
                                    disabled={disabled}
                                    name={`${prefix}sectionTitle`}
                                    required
                                />
                            </Col>
                            <Col lg={8} xs={24}>
                                <TranslatedTextAreaField
                                    {...t('variantConfiguratorDetails:fields.options.sectionDescription', {
                                        returnObjects: true,
                                    })}
                                    autoSize={{ minRows: 2, maxRows: 3 }}
                                    disabled={disabled}
                                    name={`${prefix}sectionDescription`}
                                    withContentRefinement
                                />
                            </Col>
                            <Col lg={8} xs={24}>
                                <MultiPictureWallUploadField
                                    {...t('variantConfiguratorDetails:fields.options.sectionImages', {
                                        returnObjects: true,
                                    })}
                                    disabled={disabled}
                                    extensions={allowedExtensions.image}
                                    label="Section Images"
                                    name={`${prefix}sectionImages`}
                                    required
                                />
                            </Col>
                            <Col lg={8} xs={24}>
                                <FormFields.SwitchField
                                    {...yesNoSwitch}
                                    {...t('variantConfiguratorDetails:fields.options.financing', {
                                        returnObjects: true,
                                    })}
                                    disabled={disabled}
                                    name={`${prefix}financing`}
                                />
                            </Col>
                        </>
                    )}
                </Row>
                {isActiveMeta.value && (
                    <Row gutter={14}>
                        <Col lg={8} xs={24}>
                            <FormFields.SelectField
                                {...t('variantConfiguratorDetails:fields.options.blockType', {
                                    returnObjects: true,
                                })}
                                disabled={disabled}
                                name={`${prefix}optionSettings.type`}
                                options={configurator.optionKinds}
                                required
                                showSearch
                            />
                        </Col>
                        <BlockTypeRenderer disabled={disabled} prefix={prefix} />
                    </Row>
                )}
            </Panel>
        </CollapsibleWrapper>
    );
};

const OptionsBlockForm = ({ disabled = false }: { disabled?: boolean }) => {
    const { t } = useTranslation(['variantConfiguratorDetails']);

    return (
        <ArrayField<OptionBlockValue, OptionBlockFormValues>
            name="optionsBlocks"
            render={({ name, remove, push, swap }, optionBlocks) => (
                <Space direction="vertical" style={{ width: '100%' }}>
                    {optionBlocks?.map((block, index) => (
                        <OptionBlockItem
                            key={index.toString()}
                            disabled={disabled}
                            index={index}
                            optionBlocks={optionBlocks}
                            prefix={`${name}[${index}]optionsBlockInput.`}
                            remove={remove}
                            swap={swap}
                        />
                    ))}
                    {!disabled && (
                        <Button icon={<PlusOutlined />} onClick={() => push(getOptionBlockInput())} type="primary">
                            {t('variantConfiguratorDetails:actions.options.addSetting')}
                        </Button>
                    )}
                </Space>
            )}
        />
    );
};

export default OptionsBlockForm;
